import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { ServerSummary } from '@ottd/api';

import { ConnectDialog } from '@/components/prototype/connect-dialog';
import { LiveBadge } from '@/components/prototype/live-badge';
import { TypeTile } from '@/components/prototype/type-tile';
import { Button } from '@/components/ui/button';
import { serverTypeTheme } from '@/lib/server-type';
import { GoalProgressMeter } from '@/pages/servers/goal-progress-meter';
import { serverFacts } from '@/pages/servers/server-facts';
import { bannerStyle } from '@/pages/shared';

export function ServerListCard({ server }: { server: ServerSummary }) {
  const { t } = useTranslation('server');
  const { t: tc } = useTranslation('common');
  const theme = serverTypeTheme(server.type);

  const rows = serverFacts(server);

  return (
    <article className="bevel bg-panel shadow-hard-sm flex flex-col">
      <div className="relative flex items-center gap-3 p-3" style={bannerStyle(server.type)}>
        <TypeTile type={server.type} size={42} climate={server.climate} />
        <div className="min-w-0">
          <p
            className="font-display truncate text-sm text-white"
            style={{ textShadow: '2px 2px 0 #000' }}
          >
            <Link to={`/server/${server.id}`} className="text-white">
              {server.name}
            </Link>
          </p>
          <p className="font-mono text-[11px]" style={{ color: theme.subColor }}>
            {theme.label.toUpperCase()}
          </p>
        </div>
        {server.isLive ? <LiveBadge className="absolute right-2 top-2" /> : null}
      </div>

      <div className="flex flex-col gap-1 p-3.5">
        {rows.map((row) => (
          <div key={row.key} className="flex items-baseline gap-3 py-0.5">
            <span className="text-faint w-24 shrink-0 text-[11px] uppercase tracking-wide">
              {row.key}
            </span>
            <span className={`font-mono text-[13px] ${row.tone}`}>{row.value}</span>
          </div>
        ))}
        <div className="flex items-baseline gap-3 py-0.5">
          <span className="text-faint w-24 shrink-0 text-[11px] uppercase tracking-wide">
            {t('facts.currentGame')}
          </span>
          <span className="font-mono text-[13px]">
            {server.currentGameId ? (
              <Link to={`/game/${server.currentGameId}`} className="text-green">
                #{server.currentGameId}
              </Link>
            ) : (
              <span className="text-dim">{t('facts.idle')}</span>
            )}
            <span className="text-faint">
              {' · '}
              {tc('units.online', { count: server.playersNow })}
            </span>
          </span>
        </div>
        {server.goalProgress ? (
          <div className="flex items-center gap-3 py-0.5">
            <span className="text-faint w-24 shrink-0 text-[11px] uppercase tracking-wide">
              {t('facts.progress')}
            </span>
            <GoalProgressMeter
              progress={server.goalProgress}
              type={server.type}
              compact
              className="flex-1"
            />
          </div>
        ) : null}
      </div>

      <div className="mt-auto flex gap-2 p-3.5 pt-0">
        {/* Joinable only while a game runs (legacy: no game, no button). */}
        {server.currentGameId !== null ? (
          <ConnectDialog
            address={server.address}
            inviteCode={server.inviteCode}
            serverName={server.name}
          />
        ) : null}
        <Button variant="default" size="sm" asChild>
          <Link to={`/server/${server.id}`}>{t('detail.top100')}</Link>
        </Button>
      </div>
    </article>
  );
}
